/**
 * Onboarding score — web mirror of apps/api/src/services/onboarding-score.types.ts
 * (keep the two in step) plus the labels/colours the dashboard column and the score
 * modal share, so the pill on a row and the tile in the modal can never disagree.
 *
 * The score: each buddy rating → % of its own scale; a day's score is the mean of
 * that day's ratings; the onboarding score is the mean of the day scores. Once the
 * last shadow day is over the system ADVISES deploy (score ≥ pass mark) or extend;
 * a coordinator RECORDS the decision.
 */

export type ScoreTrainingState = 'no_training' | 'not_started' | 'in_progress' | 'complete';
export type ScoreAdvice = 'deploy' | 'extend' | 'not_enough' | 'no_ratings';
export type ScoreBand = 'strong' | 'good' | 'needs_work' | 'weak';
export type ScoreDayState = 'rated' | 'no_rating_questions' | 'missed' | 'due_today' | 'upcoming' | 'no_buddy';
export type ScoreDecisionKind = 'deploy' | 'extend';

export interface ScoreQuestion {
  questionId: string;
  text: string | null;
  value: number;
  max: number;
  pct: number;
}

export interface ScoreDay {
  shadowDay: number | null;
  journeyDay: number;
  date: string;
  state: ScoreDayState;
  formId: number | null;
  formTitle: string | null;
  pct: number | null;
  onScale: { value: number; max: number } | null;
  ratedCount: number;
  band: ScoreBand | null;
  buddyName: string | null;
  filedLate: boolean;
  questions?: ScoreQuestion[];
}

export interface ScoreTopic {
  formId: number | null;
  title: string;
  pct: number;
  days: number;
  band: ScoreBand;
}

export interface ScoreDecision {
  id: number;
  decision: ScoreDecisionKind;
  advice: ScoreAdvice | null;
  scorePct: number | null;
  passMark: number;
  daysRated: number;
  daysScorable: number;
  note: string | null;
  extendedTo: string | null;
  decidedBy: string;
  decidedAt: string;
}

export interface JoinerScore {
  employeeCode: string;
  trainingState: ScoreTrainingState;
  scorePct: number | null;
  passMark: number;
  daysRated: number;
  daysScorable: number;
  daysMissed: number;
  daysExpected: number;
  lastShadowDate: string | null;
  advice: ScoreAdvice | null;
  summary: string | null;
  topics: ScoreTopic[];
  days: ScoreDay[];
  latestDecision: ScoreDecision | null;
  decisions?: ScoreDecision[];
}

export interface ScoreSettings {
  passMark: number;
  isDefault: boolean;
  updatedBy: string | null;
  updatedAt: string | null;
}

export const DEFAULT_PASS_MARK = 70;
export const MAX_DECISION_NOTE = 500;

/** Tailwind classes for a pill, keyed by tone. */
export const TONE_PILL: Record<'success' | 'warning' | 'danger' | 'neutral' | 'info', string> = {
  success: 'bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-200',
  warning: 'bg-amber-50 text-amber-800 ring-1 ring-inset ring-amber-200',
  danger: 'bg-red-50 text-red-700 ring-1 ring-inset ring-red-200',
  neutral: 'bg-gray-100 text-gray-600 ring-1 ring-inset ring-gray-200',
  info: 'bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-200',
};

export const ADVICE_META: Record<ScoreAdvice, { label: string; tone: keyof typeof TONE_PILL; hint: string }> = {
  deploy: { label: 'Ready to deploy', tone: 'success', hint: 'The onboarding score is at or above the pass mark.' },
  extend: { label: 'Extend', tone: 'warning', hint: 'The onboarding score is below the pass mark.' },
  not_enough: {
    label: 'Not enough ratings',
    tone: 'neutral',
    hint: 'Fewer than half of the shadow days were rated, so the score is not reliable.',
  },
  no_ratings: { label: 'No ratings', tone: 'neutral', hint: 'No shadow day was rated.' },
};

export const BAND_META: Record<ScoreBand, { label: string; text: string }> = {
  strong: { label: 'Strong', text: 'text-emerald-700' },
  good: { label: 'Good', text: 'text-emerald-600' },
  needs_work: { label: 'Needs work', text: 'text-amber-700' },
  weak: { label: 'Weak', text: 'text-red-600' },
};

export const DAY_STATE_LABEL: Record<ScoreDayState, string> = {
  rated: 'Rated',
  no_rating_questions: 'No rating questions',
  missed: 'Missed',
  due_today: 'Due today',
  upcoming: 'Upcoming',
  no_buddy: 'No buddy',
};

/** "86%" / "—". */
export const formatPct = (pct: number | null | undefined): string =>
  pct == null || !Number.isFinite(pct) ? '—' : `${Math.round(pct)}%`;

/**
 * A score as text: whole percent, like the rest of the dashboard — except when
 * rounding would lift a below-pass score onto the pass mark (69.6 → "70%" next to
 * "Extend"). Then it shows one decimal, so the number never contradicts the advice.
 * Used by the worklist cell and the score modal alike.
 */
export const formatScore = (pct: number | null | undefined, passMark: number): string =>
  pct != null && Number.isFinite(pct) && pct < passMark && Math.round(pct) >= passMark
    ? `${(Math.floor(pct * 10) / 10).toFixed(1)}%`
    : formatPct(pct);

/** "4.3 / 5" for an on-scale average. */
export const formatOnScale = (s: { value: number; max: number } | null | undefined): string | null =>
  s ? `${(Math.round(s.value * 10) / 10).toFixed(1).replace(/\.0$/, '')} / ${s.max}` : null;

/** "12 Sep" from YYYY-MM-DD, without timezone drift. */
export const shortDate = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return iso;
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' });
};

/** A timestamp's calendar day in IST as YYYY-MM-DD (a UTC slice would show the
 *  previous day for anything decided before 05:30 IST). */
export const istDay = (ts: string): string => {
  const t = new Date(ts);
  return Number.isNaN(t.getTime()) ? ts.slice(0, 10) : t.toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
};

/**
 * The one pill a row shows: a recorded decision wins over the advice; before
 * training is complete it's a progress state.
 */
export const scorePill = (s: JoinerScore): { label: string; tone: keyof typeof TONE_PILL; title: string } => {
  const d = s.latestDecision;
  if (d) {
    return d.decision === 'deploy'
      ? { label: 'Cleared for deployment', tone: 'success', title: `Decided ${shortDate(istDay(d.decidedAt))} by ${d.decidedBy}` }
      : {
        label: d.extendedTo ? `Extended to ${shortDate(d.extendedTo)}` : 'Extended',
        tone: 'warning',
        title: `Decided ${shortDate(istDay(d.decidedAt))} by ${d.decidedBy}`,
      };
  }
  switch (s.trainingState) {
    case 'no_training':
      return { label: 'No shadow training', tone: 'neutral', title: 'No buddy has been assigned yet.' };
    case 'not_started':
      return { label: 'Not started', tone: 'neutral', title: 'Shadow training has not started yet.' };
    case 'in_progress':
      return { label: 'In progress', tone: 'info', title: 'Shadow training is still running; the score may change.' };
    default: {
      const a = s.advice ? ADVICE_META[s.advice] : ADVICE_META.no_ratings;
      return { label: a.label, tone: a.tone, title: a.hint };
    }
  }
};

/** The caption under the score, e.g. "3 of 4 days rated · 1 missed". */
export const scoreCaption = (s: JoinerScore): string => {
  if (s.trainingState === 'no_training') return '';
  if (s.trainingState === 'not_started') return s.daysExpected ? `${s.daysExpected} shadow days ahead` : '';
  const base = s.trainingState === 'complete'
    ? `${s.daysRated} of ${s.daysExpected} days rated`
    : `${s.daysRated} of ${s.daysExpected} days so far`;
  return s.daysMissed ? `${base} · ${s.daysMissed} missed` : base;
};
